import { Box, Grid } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { NumberInput, Select } from '@factorialco/f0-react/dist/experimental'
import {
  AtsJobPostingSalaryFormatEnum,
  AtsJobPostingSalaryPeriodEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

interface CurrencyOption {
  label: string
  value: string
}

interface SalaryOptionsProps {
  salaryPeriod: AtsJobPostingSalaryPeriodEnum | null
  salaryFormat: AtsJobPostingSalaryFormatEnum | null
  salaryFromAmountInCents: number | null
  salaryToAmountInCents: number | null
  salaryCurrency: string | null
  currencyOptions: CurrencyOption[]
  onSalaryPeriodChange: (value: AtsJobPostingSalaryPeriodEnum) => void
  onSalaryFormatChange: (value: AtsJobPostingSalaryFormatEnum | null) => void
  onSalaryFromChange: (value: number | null) => void
  onSalaryToChange: (value: number | null) => void
  onSalaryCurrencyChange: (value: string | undefined) => void
}

export const SalaryOptions = ({
  salaryPeriod,
  salaryFormat,
  salaryFromAmountInCents,
  salaryToAmountInCents,
  salaryCurrency,
  currencyOptions,
  onSalaryPeriodChange,
  onSalaryFormatChange,
  onSalaryFromChange,
  onSalaryToChange,
  onSalaryCurrencyChange,
}: SalaryOptionsProps) => {
  const salaryPeriodOptions: Array<{ label: string; value: AtsJobPostingSalaryPeriodEnum }> = [
    {
      label: i18n.t('ats.job_salary_period.annual'),
      value: 'annual',
    },
    {
      label: i18n.t('ats.job_salary_period.monthly'),
      value: 'monthly',
    },
    {
      label: i18n.t('ats.job_salary_period.daily'),
      value: 'daily',
    },
  ]

  const salaryFormatOptions: Array<{ label: string; value: AtsJobPostingSalaryFormatEnum }> = [
    {
      label: i18n.t('ats.job_salary_format.range'),
      value: 'range',
    },
    {
      label: i18n.t('ats.job_salary_format.fixed_amount'),
      value: 'fixed_amount',
    },
  ]

  const isSalaryPeriod = (value: unknown): value is AtsJobPostingSalaryPeriodEnum =>
    typeof value === 'string' && salaryPeriodOptions.some((option) => option.value === value)

  const isSalaryFormat = (value: unknown): value is AtsJobPostingSalaryFormatEnum =>
    typeof value === 'string' && salaryFormatOptions.some((option) => option.value === value)

  const handleSalaryPeriodChange = (value: string) => {
    if (isSalaryPeriod(value)) {
      onSalaryPeriodChange(value)
    }
  }

  const handleSalaryFormatChange = (value: string) => {
    if (isSalaryFormat(value)) {
      onSalaryFormatChange(value)
    } else {
      onSalaryFormatChange(null)
    }
  }

  const handleSalaryFromChange = (value: number | null) => {
    onSalaryFromChange(value === null ? null : Number(value))
  }

  const handleSalaryToChange = (value: number | null) => {
    onSalaryToChange(value === null ? null : Number(value))
  }

  return (
    <>
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 1, xs: 4 }}>
          <Select
            label={i18n.t('ats.job_posting_settings.salary.salary_period_label')}
            name="salaryPeriod"
            placeholder={i18n.t('common.select_basic')}
            onChange={(value: string) => {
              handleSalaryPeriodChange(value)
            }}
            size="md"
            value={salaryPeriod}
            options={salaryPeriodOptions}
          />
        </Box>
        <Box gridColSpan={{ xxs: 1, xs: 4 }}>
          <Select
            label={i18n.t('ats.job_posting_settings.salary.salary_format_label')}
            name="salaryFormat"
            placeholder={i18n.t('common.select_basic')}
            onChange={(value: string) => {
              handleSalaryFormatChange(value)
            }}
            size="md"
            value={salaryFormat ?? undefined}
            options={salaryFormatOptions}
          />
        </Box>
        <Box gridColSpan={{ xxs: 1, xs: 4 }}>
          <F0Select
            label={i18n.t('ats.job_posting_settings.salary.currency_label')}
            name="salaryCurrency"
            placeholder={i18n.t('common.select')}
            size="md"
            value={salaryCurrency ?? undefined}
            onChange={(value: string | undefined) => onSalaryCurrencyChange(value)}
            showSearchBox
            searchEmptyMessage={i18n.t('ats.vacancies.creation.search_no_results_found')}
            options={currencyOptions}
          />
        </Box>
      </Grid>
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 1, xs: 6 }}>
          <NumberInput
            label={
              salaryFormat === 'range'
                ? i18n.t('ats.job_posting_settings.salary.salary_from_amount_range')
                : i18n.t('ats.job_posting_settings.salary.salary_from_amount')
            }
            locale={i18n.locale}
            placeholder={i18n.t('common.select_basic')}
            value={salaryFromAmountInCents}
            onChange={handleSalaryFromChange}
            units={salaryCurrency ?? undefined}
            size="md"
          />
        </Box>
        <Box gridColSpan={{ xxs: 1, xs: 6 }}>
          {salaryFormat === 'range' && (
            <NumberInput
              label={i18n.t('ats.job_posting_settings.salary.salary_to_amount')}
              locale={i18n.locale}
              value={salaryToAmountInCents}
              onChange={handleSalaryToChange}
              units={salaryCurrency ?? undefined}
              size="md"
            />
          )}
        </Box>
      </Grid>
    </>
  )
}
